import React from 'react';
import { useLanguage } from './LanguageContext';
import { translations } from '../data/translations';
import { ChevronDown, Award } from 'lucide-react';
import { ArabicPattern } from './ArabicPattern';

export const Hero = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const isRTL = language === 'ar';

  const scrollToServices = () => {
    const element = document.getElementById('services');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-b from-black via-gray-900 to-black">
      {/* Arabic Pattern Background */}
      <ArabicPattern />
      
      {/* Animated Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-10 w-72 h-72 bg-teal-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-teal-600/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gray-800/30 rounded-full blur-3xl"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 text-center">
        {/* Badge */}
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-teal-500/10 border border-teal-500/30 mb-8 animate-fade-in">
          <Award className="w-5 h-5 text-teal-400" />
          <span className="text-teal-400 font-medium">{t.hero.pioneer}</span>
        </div>

        {/* Main Title */}
        <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-6 animate-fade-in" style={{ animationDelay: '0.2s' }}>
          {t.hero.title}
        </h1>
        
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-light text-teal-400 mb-8 animate-fade-in" style={{ animationDelay: '0.4s' }}>
          {t.hero.subtitle}
        </h2>

        <p className={`text-lg sm:text-xl text-gray-300 max-w-3xl mx-auto mb-12 leading-relaxed animate-fade-in ${isRTL ? 'text-right' : 'text-left'}`} style={{ animationDelay: '0.6s' }}>
          {t.hero.description}
        </p>

        {/* CTA Button */}
        <button
          onClick={scrollToServices}
          className="group relative inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-teal-500 to-teal-600 text-white font-semibold rounded-lg overflow-hidden transition-all duration-300 hover:shadow-xl hover:shadow-teal-500/50 hover:scale-105 animate-fade-in"
          style={{ animationDelay: '0.8s' }}
        >
          <span className="relative z-10">{t.hero.cta}</span>
          <ChevronDown className="w-5 h-5 relative z-10 group-hover:animate-bounce" />
          <div className="absolute inset-0 bg-gradient-to-r from-teal-600 to-teal-700 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
        </button>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 mt-20 animate-fade-in" style={{ animationDelay: '1s' }}>
          <div className="text-center">
            <div className="text-4xl font-bold text-teal-400 mb-2">15+</div>
            <div className="text-gray-400">{language === 'en' ? 'Years Experience' : 'سنة خبرة'}</div>
          </div>
          <div className="text-center">
            <div className="text-4xl font-bold text-teal-400 mb-2">6</div>
            <div className="text-gray-400">{language === 'en' ? 'Business Divisions' : 'أقسام تجارية'}</div>
          </div>
          <div className="text-center">
            <div className="text-4xl font-bold text-teal-400 mb-2">100%</div>
            <div className="text-gray-400">{language === 'en' ? 'Qatari Brand' : 'علامة قطرية'}</div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <ChevronDown className="w-6 h-6 text-teal-400" />
      </div>
    </section>
  );
};